import { useState } from 'react';
import type { PlanLine, SprintPlan, SprintView } from '../api/types.js';
import { usePlanDecisions, useSprintPlan } from '../hooks/use-sprint.js';

// The next sprint's plan (ADR-034 step 4). Prepare asks the board's sprint session for a plan, with who is away; the
// plan comes back grouped by sprint, each line editable, the load of each sprint against the capacity recomputed from
// the edits. Apply the plan writes the kept lines to Jira; Dismiss writes nothing. Members read, admins act.

const POINTS = [0.5, 1, 2, 3, 5, 8, 13, 21];
const BACKLOG = '';
const control = 'rounded-md border px-1.5 py-0.5 text-xs';
const controlStyle = { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' };
const button = 'rounded-md border px-2.5 py-1 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60';
const primary = { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-text)', outlineColor: 'var(--color-brand)' };
const quiet = { borderColor: 'var(--color-border)', color: 'var(--color-text-2)', outlineColor: 'var(--color-brand)' };

type Edit = PlanLine & { keep: boolean };

/** Each sprint's load once the kept lines are applied: its total and each person's share */
export function plannedLoad(v: SprintView, plan: SprintPlan, edits: Edit[]): Map<string, { total: number; people: Map<string, number> }> {
  const out = new Map<string, { total: number; people: Map<string, number> }>();
  for (const s of v.upcoming ?? []) out.set(s.name, { total: s.total.points, people: new Map(s.people.map((p) => [p.name, p.total.points])) });
  const add = (sprint: string | undefined, person: string, points: number) => {
    const load = sprint ? out.get(sprint) : undefined;
    if (!load || !points) return;
    load.total = Math.round((load.total + points) * 10) / 10;
    load.people.set(person, Math.round(((load.people.get(person) ?? 0) + points) * 10) / 10);
  };
  for (const e of edits.filter((x) => x.keep)) {
    const was = plan.tickets[e.key] ?? { summary: '' };
    add(was.sprint, was.assignee ?? 'Unassigned', -(was.storyPoints ?? 0));
    add(e.sprint, e.assignee ?? was.assignee ?? 'Unassigned', e.storyPoints ?? was.storyPoints ?? 0);
  }
  return out;
}

/** What a line changes, in a few words: « W43 → W44, Ewan → Kamel, no estimate → 3 SP » */
function change(e: PlanLine, was: SprintPlan['tickets'][string] | undefined): string {
  const parts: string[] = [];
  const from = was?.sprint || 'backlog';
  const to = e.sprint || 'backlog';
  if (from !== to) parts.push(`${from} → ${to}`);
  if (e.assignee && e.assignee !== was?.assignee) parts.push(`${was?.assignee ?? 'unassigned'} → ${e.assignee}`);
  if (e.storyPoints !== undefined && e.storyPoints !== was?.storyPoints) parts.push(`${was?.storyPoints !== undefined ? `${was.storyPoints} SP` : 'no estimate'} → ${e.storyPoints} SP`);
  if (e.comment) parts.push('a comment');
  return parts.join(', ') || 'no change';
}

function Line({ e, plan, people, site, editable, result, onChange }: {
  e: Edit; plan: SprintPlan; people: string[]; site: string; editable: boolean; result?: { ok: boolean; error?: string }; onChange: (next: Edit) => void;
}) {
  const was = plan.tickets[e.key];
  return (
    <li className="border-t py-2 text-sm" style={{ borderColor: 'var(--color-border)', opacity: e.keep ? 1 : 0.55 }} data-testid="plan-line">
      <div className="flex flex-wrap items-baseline gap-x-2">
        {editable && <input type="checkbox" checked={e.keep} onChange={() => onChange({ ...e, keep: !e.keep })} aria-label={`Keep ${e.key}`} />}
        <a href={`${site}/browse/${e.key}`} target="_blank" rel="noreferrer" className="font-medium underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{e.key}</a>
        <span className="min-w-0 break-words" style={{ color: 'var(--color-text)' }}>{e.summary}</span>
        <span className="text-xs" style={{ color: 'var(--color-text-2)' }}>{change(e, was)}</span>
        {result && <span className="text-xs" style={{ color: result.ok ? 'var(--color-success)' : 'var(--color-error)' }}>{result.ok ? 'written' : `not written: ${result.error ?? ''}`}</span>}
      </div>
      {e.reason && <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-3)' }}>{e.reason}</p>}
      {editable && e.keep && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <select value={e.sprint} onChange={(x) => onChange({ ...e, sprint: x.target.value, comment: x.target.value === BACKLOG ? e.comment : undefined })} aria-label={`Sprint of ${e.key}`} className={control} style={controlStyle}>
            {plan.sprints.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            <option value={BACKLOG}>Backlog</option>
          </select>
          <select value={e.assignee ?? ''} onChange={(x) => onChange({ ...e, assignee: x.target.value || undefined })} aria-label={`Assignee of ${e.key}`} className={control} style={controlStyle}>
            <option value="">{was?.assignee ? `Keep ${was.assignee}` : 'Unassigned'}</option>
            {people.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={e.storyPoints ?? ''} onChange={(x) => onChange({ ...e, storyPoints: x.target.value ? Number(x.target.value) : undefined })} aria-label={`Story points of ${e.key}`} className={control} style={controlStyle}>
            <option value="">{was?.storyPoints !== undefined ? `Keep ${was.storyPoints} SP` : 'No estimate'}</option>
            {POINTS.map((p) => <option key={p} value={p}>{`${p} SP`}</option>)}
          </select>
          {e.sprint === BACKLOG && (
            <textarea rows={2} value={e.comment ?? ''} onChange={(x) => onChange({ ...e, comment: x.target.value })} placeholder="Comment to post on the ticket" aria-label={`Comment on ${e.key}`}
              className={`${control} w-full`} style={controlStyle} />
          )}
        </div>
      )}
    </li>
  );
}

function Plan({ serverId, v, plan, isAdmin, site }: { serverId: string; v: SprintView; plan: SprintPlan; isAdmin: boolean; site: string }) {
  const decisions = usePlanDecisions(serverId, v.board.id);
  const open = plan.status === 'pending' || plan.status === 'failed';
  const editable = isAdmin && open;
  const [edits, setEdits] = useState<Edit[]>(() => (plan.lines ?? []).map((l) => ({ ...l, keep: true })));
  const people = [...new Set([...v.people.map((p) => p.name).filter((n) => n !== 'Unassigned'), ...(plan.lines ?? []).flatMap((l) => (l.assignee ? [l.assignee] : []))])].sort();
  const loads = plannedLoad(v, plan, edits);
  const cap = plan.capacity ?? v.capacity;
  const results = new Map((plan.results ?? []).map((r) => [r.key, r]));
  const groups = [...plan.sprints.map((s) => s.name), BACKLOG].map((name) => ({ name, lines: edits.filter((e) => e.sprint === name) })).filter((g) => g.lines.length);
  const set = (key: string) => (next: Edit) => setEdits((all) => all.map((e) => (e.key === key ? next : e)));
  const kept = edits.filter((e) => e.keep);
  return (
    <div className="space-y-4">
      {plan.notes && <p className="max-w-[72ch] text-sm" style={{ color: 'var(--color-text-2)' }}>{plan.notes}</p>}
      {plan.away.length > 0 && <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>{`Away: ${plan.away.join(', ')}`}</p>}
      {groups.map((g) => {
        const load = loads.get(g.name);
        const over = !!load && cap !== undefined && load.total > cap;
        return (
          <div key={g.name || 'backlog'} data-testid="plan-group">
            <div className="flex flex-wrap items-baseline gap-x-3">
              <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>{g.name || 'Backlog'}</h4>
              {load && <span className="text-xs tabular-nums" style={{ color: over ? 'var(--color-warn)' : 'var(--color-text-2)' }}>{cap !== undefined ? `${load.total} of ${cap} SP` : `${load.total} SP`}</span>}
              {load && <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{[...load.people].filter(([, p]) => p > 0).map(([n, p]) => `${n} ${p}`).join(', ')}</span>}
            </div>
            <ul className="mt-1">{g.lines.map((e) => <Line key={e.key} e={e} plan={plan} people={people} site={site} editable={editable} result={results.get(e.key)} onChange={set(e.key)} />)}</ul>
          </div>
        );
      })}
      {plan.status === 'applied' && <p className="text-sm" style={{ color: 'var(--color-success)' }}>{`Applied: ${plan.results?.length ?? 0} tickets written to Jira.`}</p>}
      {plan.status === 'failed' && plan.error && <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>{plan.error}</p>}
      {editable && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={button} style={primary} disabled={!kept.length || decisions.apply.isPending} onClick={() => decisions.apply.mutate(kept.map(({ keep: _keep, ...l }) => l))}>
            {decisions.apply.isPending ? 'Writing to Jira…' : `Apply the plan (${kept.length})`}
          </button>
          <button type="button" className={button} style={quiet} disabled={decisions.dismiss.isPending} onClick={() => decisions.dismiss.mutate()}>Dismiss</button>
        </div>
      )}
      {decisions.apply.isError && <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>{`Not applied: ${decisions.apply.error.message}`}</p>}
    </div>
  );
}

/** The board's next sprint plan: Prepare, then the plan to decide on */
export function SprintPlanSection({ serverId, v, isAdmin }: { serverId: string; v: SprintView; isAdmin: boolean }) {
  const { data } = useSprintPlan(serverId, v.board.id, !!v.sprint);
  const decisions = usePlanDecisions(serverId, v.board.id);
  const [asking, setAsking] = useState(false);
  const [away, setAway] = useState<string[]>([]);
  const plan = data?.plan;
  const site = new URL(v.url).origin;
  const people = v.people.map((p) => p.name).filter((n) => n !== 'Unassigned');
  const live = plan && !['applied', 'dismissed'].includes(plan.status);
  if (!v.sprint || (!live && !isAdmin)) return null;
  return (
    <section className="mb-6" aria-label="Next sprint plan">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h3 className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>Next sprint plan</h3>
        {plan?.status === 'queued' && <span className="text-sm" style={{ color: 'var(--color-text-3)' }}>Preparing the plan…</span>}
        {plan?.status === 'applying' && <span className="text-sm" style={{ color: 'var(--color-text-3)' }}>Writing to Jira…</span>}
        {isAdmin && !live && !asking && <button type="button" className={button} style={quiet} onClick={() => setAsking(true)}>Prepare the next sprint</button>}
      </div>
      {asking && !live && (
        <div className="mt-2 space-y-2">
          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Who is away">
            <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>Who is away:</span>
            {people.map((p) => {
              const on = away.includes(p);
              return (
                <button key={p} type="button" aria-pressed={on} onClick={() => setAway((a) => (on ? a.filter((x) => x !== p) : [...a, p]))}
                  className="rounded-full border px-2.5 py-0.5 text-xs" style={on ? primary : quiet}>{p}</button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <button type="button" className={button} style={primary} disabled={decisions.prepare.isPending} onClick={() => decisions.prepare.mutate(away, { onSuccess: () => setAsking(false) })}>
              {decisions.prepare.isPending ? 'Sending…' : 'Prepare'}
            </button>
            <button type="button" className={button} style={quiet} onClick={() => setAsking(false)}>Cancel</button>
          </div>
          {decisions.prepare.isError && <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>{`Not prepared: ${decisions.prepare.error.message}`}</p>}
        </div>
      )}
      {plan && live && plan.status !== 'queued' && <div className="mt-2"><Plan key={plan.requestedAt} serverId={serverId} v={v} plan={plan} isAdmin={isAdmin} site={site} /></div>}
    </section>
  );
}
